import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  fetchPublicRuntimeConfig,
  fetchRuntimeConfig,
  type LoginMode,
} from "../services/runtimeConfig";

type RuntimeConfigState = {
  loginMode: LoginMode;
  features: Record<string, boolean>;
  isPublicReady: boolean;
  loadAuthenticatedConfig: (accessToken: string) => Promise<void>;
  clearAuthenticatedConfig: () => void;
};

const RuntimeConfigCtx = createContext<RuntimeConfigState | null>(null);

export function RuntimeConfigProvider({ children }: { children: React.ReactNode }) {
  const [loginMode, setLoginMode] = useState<LoginMode>("both");
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [isPublicReady, setPublicReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    void fetchPublicRuntimeConfig()
      .then((config) => {
        if (mounted) {
          setLoginMode(config.login_mode);
        }
      })
      .finally(() => {
        if (mounted) {
          setPublicReady(true);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const loadAuthenticatedConfig = useCallback(async (accessToken: string) => {
    try {
      const config = await fetchRuntimeConfig(accessToken);
      setFeatures(config.features ?? {});
    } catch {
      setFeatures({});
    }
  }, []);

  const clearAuthenticatedConfig = useCallback(() => {
    setFeatures({});
  }, []);

  const value = useMemo(
    () => ({
      loginMode,
      features,
      isPublicReady,
      loadAuthenticatedConfig,
      clearAuthenticatedConfig,
    }),
    [loginMode, features, isPublicReady, loadAuthenticatedConfig, clearAuthenticatedConfig]
  );

  return <RuntimeConfigCtx.Provider value={value}>{children}</RuntimeConfigCtx.Provider>;
}

export function useRuntimeConfig() {
  const ctx = useContext(RuntimeConfigCtx);
  if (!ctx) throw new Error("useRuntimeConfig must be used within RuntimeConfigProvider");
  return ctx;
}
